// Content Assets — cross-project file manager for ContentIQ uploads
// (Edward, 2026-08-11: own nav page, shared DataTable, soft delete).
//
// Assets belong to exactly one content project; client/program/component here
// are DERIVED from that project's attachments — so the filters follow
// attachments, and the one real association edit is moving an asset to a
// different project. Delete is a soft delete with an Undo toast; the file
// itself stays in S3 until an explicit recycle-bin purge, so restore brings
// it back intact.
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import SearchableSelect from '../../components/SearchableSelect';
import DataTable from '../../components/DataTable';
import Toast from '../../components/Toast';
import { useSoftDelete } from '../../hooks/useSoftDelete';
import { fmtDate } from '../../components/ZbSummary';
import { downloadAssetBlob, MIME_LABELS, fmtSize } from '../../utils/ciqAssets';
import { buildStatusGroupMap, buildProgramOptionGroups } from '../../utils/statusGroups';
import { useNavigate } from 'react-router-dom';
import { FONTS, INK, NEUTRAL, RADII, STATE, buttonLink, displayFont } from '../../utils/wizardTheme';

const API = process.env.REACT_APP_API_URL;

export default function ContentIQAssets() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [programs, setPrograms] = useState([]);
  // Closed programs nest into a collapsible section on the Program picker
  // below instead of mixing into the flat list — see src/utils/statusGroups.js.
  const [programStatuses, setProgramStatuses] = useState([]);
  const [err, setErr] = useState('');
  const [filter, setFilter] = useState({ q: '', client_id: '', program_id: '' });
  const [sortField, setSortField] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');

  const load = useCallback(() => {
    Promise.allSettled([
      axios.get(`${API}/contentiq/assets`),
      axios.get(`${API}/contentiq/projects/`),
      axios.get(`${API}/clients/`),
      axios.get(`${API}/programs/`),
      axios.get(`${API}/lookup-values/?category=program_status`),
    ]).then(([a, p, c, pr, ps]) => {
      if (a.status === 'fulfilled') { setRows(a.value.data); setErr(''); }
      else setErr('Could not load assets.');
      if (p.status === 'fulfilled') setProjects(p.value.data);
      if (c.status === 'fulfilled') setClients(c.value.data);
      if (pr.status === 'fulfilled') setPrograms(pr.value.data);
      if (ps.status === 'fulfilled') setProgramStatuses(ps.value.data);
    });
  }, []);
  useEffect(() => { load(); }, [load]);

  const { toast, dismissToast, deleteWithUndo } = useSoftDelete('content_asset', load);

  // Closed programs nest into a collapsible section on the Program filter
  // below instead of mixing into the flat list — see src/utils/statusGroups.js.
  const filterProgramCandidates = filter.client_id
    ? programs.filter(pr => String(pr.client_id) === filter.client_id)
    : programs;
  const filterProgramOptionGroups = buildProgramOptionGroups(filterProgramCandidates, buildStatusGroupMap(programStatuses));

  const visible = rows.filter(r => {
    if (filter.client_id && !(r.attachments || []).some(a => String(a.client_id) === filter.client_id)) return false;
    if (filter.program_id && !(r.attachments || []).some(a => String(a.program_id) === filter.program_id)) return false;
    const q = filter.q.trim().toLowerCase();
    if (q) {
      const hay = [r.title, r.original_filename, r.project_name,
        ...(r.attachments || []).flatMap(a => [a.client_name, a.program_name, a.component_name]),
      ].filter(Boolean).join(' ').toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });

  const handleSort = (field) => {
    if (field === sortField) setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortField(field); setSortDir('asc'); }
  };
  const sorted = [...visible].sort((a, b) => {
    const dir = sortDir === 'desc' ? -1 : 1;
    const va = a[sortField], vb = b[sortField];
    if (va == null && vb == null) return 0;
    if (va == null) return 1;
    if (vb == null) return -1;
    if (typeof va === 'number') return (va - vb) * dir;
    return String(va).localeCompare(String(vb)) * dir;
  });

  const remove = (r) => deleteWithUndo(r, r.title, () =>
    axios.delete(`${API}/contentiq/projects/${r.project_id}/assets/${r.id}`));

  const move = async (r, projectId) => {
    if (!projectId) return;
    const target = projects.find(p => String(p.id) === projectId);
    if (!window.suppressConfirm && !window.confirm(`Move “${r.title}” to “${target ? target.name : 'that project'}”?`)) return;
    try {
      await axios.put(`${API}/contentiq/assets/${r.id}/move`, { project_id: Number(projectId) });
      load();
    } catch (e) { setErr(e.response?.data?.detail || 'Move failed.'); }
  };

  const columns = [
    {
      key: 'title', header: 'File', width: 240, sortable: true,
      render: r => (
        <span style={{ fontWeight: 600 }}>
          📄 {r.title}
          {r.original_filename && r.original_filename !== r.title && (
            <div style={{ color: INK.muted, fontSize: '11px', fontWeight: 400 }}>{r.original_filename}</div>
          )}
        </span>
      ),
    },
    { key: 'mimetype', header: 'Type', width: 60, render: r => MIME_LABELS[r.mimetype] || (r.mimetype || '—').split('/').pop() },
    { key: 'size_bytes', header: 'Size', width: 75, sortable: true, nowrap: true, render: r => fmtSize(r.size_bytes) },
    { key: 'project_name', header: 'Content Project', width: 180, sortable: true },
    {
      key: 'attachments', header: 'Client · Program › Component', width: 260,
      render: r => ((r.attachments || []).length === 0
        ? <span style={{ color: INK.muted }}>—</span>
        : (r.attachments || []).map((a, i) => (
          <div key={i} style={{ fontSize: '12px' }}>
            {a.client_name ? `${a.client_name} · ` : ''}{a.program_name}
            {a.component_name ? ` › ${a.component_name}` : ''}
          </div>
        ))),
    },
    {
      key: 'created_at', header: 'Uploaded', width: 140, sortable: true, nowrap: true,
      render: r => (
        <span style={{ fontSize: '12px' }}>
          {fmtDate(r.created_at)}
          {r.uploaded_by_name && <div style={{ color: INK.muted, fontSize: '11px' }}>{r.uploaded_by_name}</div>}
        </span>
      ),
    },
    {
      key: 'actions', header: '', width: 200,
      render: r => (
        <span style={{ whiteSpace: 'nowrap' }}>
          <button onClick={() => downloadAssetBlob(r.project_id, r)} title="Download"
            style={{ border: 'none', background: 'none', cursor: 'pointer' }}>⬇</button>
          <select value="" onChange={e => move(r, e.target.value)}
            style={{ fontSize: '12px', maxWidth: '110px', marginRight: '4px' }}>
            <option value="">Move to…</option>
            {projects.filter(p => p.id !== r.project_id).map(p => (
              <option key={p.id} value={String(p.id)}>{p.name}</option>
            ))}
          </select>
          <button onClick={() => remove(r)} title="Delete (recoverable from the Recycle Bin)"
            style={{ border: 'none', background: 'none', cursor: 'pointer', color: INK.muted }}>✕</button>
        </span>
      ),
    },
  ];

  return (
    <div style={{ padding: '20px 24px', maxWidth: '1200px', fontFamily: FONTS.body, color: INK.primary }}>
      <button onClick={() => navigate('/contentiq')} style={{ ...buttonLink, fontSize: '13px', marginBottom: '8px' }}>
        ← ContentIQ Home
      </button>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '4px' }}>
        <h1 style={{ margin: 0, ...displayFont('24px', 700) }}>Content assets</h1>
        <span style={{
          background: STATE.gate.tint, color: STATE.gate.deep, borderRadius: '10px',
          padding: '2px 10px', fontSize: '12px', fontWeight: 600,
        }}>
          Pilot
        </span>
      </div>
      <p style={{ color: INK.secondary, fontSize: '13px', marginTop: 0 }}>
        Every file uploaded across content projects. Client / program / component come from the
        owning project’s attachments; upload new files from a project’s Client assets step.
      </p>

      <div style={{ display: 'flex', gap: '8px', marginBottom: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
        <input value={filter.q} onChange={e => setFilter(f => ({ ...f, q: e.target.value }))}
          placeholder="🔍 Search files, projects…"
          style={{ padding: '6px 8px', border: `1px solid ${NEUTRAL.control}`, borderRadius: RADII.small, fontSize: '13px', width: '240px', fontFamily: FONTS.body }} />
        <div style={{ width: '220px' }}>
          <SearchableSelect
            options={clients.map(c => ({ value: String(c.id), label: c.name }))}
            value={filter.client_id}
            onChange={v => setFilter(f => ({ ...f, client_id: v, program_id: '' }))}
            placeholder="— all clients —" />
        </div>
        <div style={{ width: '260px' }}>
          <SearchableSelect
            options={filterProgramOptionGroups.options}
            closedOptions={filterProgramOptionGroups.closedOptions}
            value={filter.program_id}
            onChange={v => setFilter(f => ({ ...f, program_id: v }))}
            placeholder="— all programs —" />
        </div>
        {(filter.q || filter.client_id || filter.program_id) && (
          <button onClick={() => setFilter({ q: '', client_id: '', program_id: '' })}
            style={{ ...buttonLink, fontSize: '12px', fontWeight: 400, color: INK.secondary }}>
            ✕ Clear
          </button>
        )}
        <span style={{ marginLeft: 'auto', color: INK.muted, fontSize: '12px' }}>
          {visible.length} of {rows.length} file{rows.length === 1 ? '' : 's'}
        </span>
      </div>
      {err && <div style={{ color: STATE.danger.text, fontSize: '13px', marginBottom: '8px' }}>{err}</div>}

      <DataTable
        rows={sorted}
        columns={columns}
        sort={{ field: sortField, dir: sortDir, onSort: handleSort }}
        emptyText="No files yet — upload them from a project's Client assets step."
      />
      <Toast toast={toast} onDismiss={dismissToast} />
    </div>
  );
}
